"use client"

import { useState, useEffect } from "react"
import { Card, CardContent } from "../ui/card"
import { Button } from "../ui/button"
import { Progress } from "../ui/progress"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Calendar, Search, Play } from "lucide-react"
import { Input } from "../ui/input"
import { Badge } from "../ui/badge"
import { formationAPI, inscriptionAPI } from "../../services/api"
import { useAuth } from "../../context/AuthContext"

// Type definitions based on the database structure
interface FormationProgress {
  id: number
  name: string
  image: string | null
  categoryName: string | null
  progress: string // "75%" format from API
  lastUpdate: string | null
  status: "En cours" | "Terminée"
}

// Transformed course interface for component use
interface TransformedCourse {
  id: string
  title: string
  image: string
  category: string
  instructor: string
  progress: number // numeric percentage
  lastAccessed: string
  status: "completed" | "in-progress"
}

interface CourseRequest {
  id: number
  formationId: number
  formationName: string | null
  fullName: string
  phoneNumber: string
  email: string
  status: "Approuvée" | "En attente" | "Rejetée"
  demandeDate: string
  approbationDate: string | null
}

export default function DashboardCourses() {
  const { user } = useAuth()
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [formations, setFormations] = useState<TransformedCourse[]>([])
  const [courseRequests, setCourseRequests] = useState<CourseRequest[]>([])

  // Get current user ID from auth context
  const getCurrentUserId = () => {
    return user?.id || null
  }

  useEffect(() => {
    if (user?.id) {
      fetchUserFormations()
      fetchUserRequests()
    }
  }, [user?.id])

  const fetchUserFormations = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const userId = getCurrentUserId()
      
      if (!userId) {
        setError('Utilisateur non connecté')
        return
      }
      
      const response = await formationAPI.getFormationProgress(userId)
      
      // Check if response has data property
      const formationsData = response.data?.data || response.data || []
      
      // Transform API response to match component expectations
      const transformedData: TransformedCourse[] = formationsData.map((item: FormationProgress, index: number) => {
        try {
          
          return {
            id: item.id.toString(),
            title: item.name,
            image: item.image ? `${item.image}` : "/placeholder.svg",
            category: item.categoryName || "Formation",
            instructor: "Elille academy", // Static as requested
            progress: parseInt(item.progress.replace('%', '')), // Convert "75%" to 75
            lastAccessed: item.lastUpdate ? new Date(item.lastUpdate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            status: item.status === "Terminée" ? "completed" : "in-progress",
          }
        } catch (itemError) {
          console.error(`Error processing item ${index}:`, itemError, item)
          // Return a default item to prevent crash
          return {
            id: (item.id || index).toString(),
            title: item.name || "Formation sans titre",
            image: "/placeholder.svg",
            category: "Formation",
            instructor: "Elille academy",
            progress: 0,
            lastAccessed: new Date().toISOString().split('T')[0],
            status: "in-progress" as const,
          }
        }
      })
      
      setFormations(transformedData)
    } catch (err) {
      console.error('Error fetching formations:', err)
      setError('Erreur lors du chargement des formations')
    } finally {
      setLoading(false)
    }
  }

  const fetchUserRequests = async () => {
    try {
      const userId = getCurrentUserId()
      if (!userId) return

      const response = await inscriptionAPI.getUserRequests(userId)
      setCourseRequests(response.data)
      
    } catch (err) {
      console.error('Error fetching course requests:', err)
    }
  }

  // Filter courses by search term
  const filteredCourses = formations.filter(
    (course) =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleResetAndWatch = async (formationId: number) => {
    const userId = getCurrentUserId();
    if (!userId) return;

    try {
      await formationAPI.resetFormationProgress({ userId, formationId });
      // Optionally refetch progress after reset
      await fetchUserFormations();
      // Navigate to watch page
      window.location.href = `/dashboard/watch/${formationId}`;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation de la progression:', error);
      alert("Une erreur est survenue lors de la réinitialisation de la formation.");
    }
  };

  if (!user) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <p className="text-gray-500 dark:text-gray-400">Veuillez vous connecter pour voir vos formations</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Chargement de vos formations...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <h2 className="text-xl font-bold text-red-600 mb-4">Erreur</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{error}</p>
          <Button onClick={() => {
            fetchUserFormations()
            fetchUserRequests()
          }}>
            Réessayer
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container px-4 py-8 md:px-6 md:py-12">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-serif font-bold tracking-tight">Mes formations</h1>
            <p className="text-gray-500 dark:text-gray-400">Suivez votre progression et accédez à vos formations</p>
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500 dark:text-gray-400" />
            <Input
              type="search"
              placeholder="Rechercher une formation..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-4 md:w-auto">
            <TabsTrigger value="all">Toutes ({formations.length})</TabsTrigger>
            <TabsTrigger value="in-progress">
              En cours ({formations.filter(f => f.status === "in-progress").length})
            </TabsTrigger>
            <TabsTrigger value="completed">
              Terminées ({formations.filter(f => f.status === "completed").length})
            </TabsTrigger>
            <TabsTrigger value="requests">Mes demandes</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            {filteredCourses.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  {searchTerm ? "Aucune formation trouvée pour votre recherche" : "Vous n'êtes inscrit à aucune formation"}
                </div>
                {!searchTerm && (
                  <Button asChild>
                    <a href="/courses">Explorer les formations</a>
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses.map((course) => (
                  <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="relative h-40 w-full group">
                      <img 
                        src={course.image || "/placeholder.svg"} 
                        alt={course.title} 
                        className="object-cover w-full h-full absolute inset-0" 
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <Play className="w-16 h-16 text-white opacity-80" />
                      </div>
                      <div className="absolute top-0 right-0 m-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            course.status === "completed" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {course.status === "completed" ? "Terminée" : "En cours"}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="mb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                          {course.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold line-clamp-2 h-12 mb-2">{course.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Par {course.instructor}</p>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span>Progression</span>
                          <span>{course.progress}%</span>
                        </div>
                        <Progress value={course.progress} className="h-2" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-xs text-gray-500">
                          <Calendar className="h-3 w-3 mr-1" />
                          <span>{new Date(course.lastAccessed).toLocaleDateString("fr-FR")}</span>
                        </div>
                        {course.status === "completed" ? (
                          <Button size="sm" variant="outline" asChild>
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault(); // prevent default <a> navigation
                                handleResetAndWatch(parseInt(course.id));
                              }}
                            >
                              Revoir
                            </a>
                          </Button>
                        ) : (
                          <Button size="sm" asChild>
                            <a href={`/dashboard/watch/${course.id}`}>Continuer</a>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="in-progress" className="mt-6">
            {filteredCourses.filter((course) => course.status === "in-progress").length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                Aucune formation en cours
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses
                  .filter((course) => course.status === "in-progress")
                  .map((course) => (
                    <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="relative h-40 w-full group">
                        <img
                          src={course.image || "/placeholder.svg"}
                          alt={course.title}
                          className="object-cover w-full h-full absolute inset-0"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <Play className="w-16 h-16 text-white opacity-80" />
                        </div>
                        <div className="absolute top-0 right-0 m-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            En cours
                          </span>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <div className="mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            {course.category}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold line-clamp-2 h-12 mb-2">{course.title}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Par {course.instructor}</p>
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span>Progression</span>
                            <span>{course.progress}%</span>
                          </div>
                          <Progress value={course.progress} className="h-2" />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="h-3 w-3 mr-1" />
                            <span>{new Date(course.lastAccessed).toLocaleDateString("fr-FR")}</span>
                          </div>
                          <Button size="sm" asChild>
                            <a href={`/dashboard/watch/${course.id}`}>Continuer</a>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed" className="mt-6">
            {filteredCourses.filter((course) => course.status === "completed").length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                Aucune formation terminée
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses
                  .filter((course) => course.status === "completed")
                  .map((course) => (
                    <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="relative h-40 w-full group">
                        <img
                          src={course.image || "/placeholder.svg"}
                          alt={course.title}
                          className="object-cover w-full h-full absolute inset-0"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <Play className="w-16 h-16 text-white opacity-80" />
                        </div>
                        <div className="absolute top-0 right-0 m-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Terminée
                          </span>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <div className="mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            {course.category}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold line-clamp-2 h-12 mb-2">{course.title}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Par {course.instructor}</p>
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span>Progression</span>
                            <span>{course.progress}%</span>
                          </div>
                          <Progress value={course.progress} className="h-2" />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="h-3 w-3 mr-1" />
                            <span>{new Date(course.lastAccessed).toLocaleDateString("fr-FR")}</span>
                          </div>
                          <Button size="sm" variant="outline" asChild>
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault(); // prevent default link navigation
                                handleResetAndWatch(parseInt(course.id));
                              }}
                            >
                              Revoir
                            </a>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Demandes d'inscription en attente</h3>
              {courseRequests.filter((request) => request.status === "En attente").length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {courseRequests
                    .filter((request) => request.status === "En attente")
                    .map((request) => (
                      <Card key={request.id} className="overflow-hidden border-l-4 border-l-yellow-400">
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                              <Badge variant="outline" className="mb-2 bg-yellow-50 text-yellow-700 border-yellow-200">
                                En attente
                              </Badge>
                              <h4 className="text-lg font-bold">{request.formationName}</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Demande soumise le {new Date(request.demandeDate).toLocaleDateString("fr-FR")}
                              </p>
                            </div>
                            <Button variant="outline" size="sm">
                              Voir les détails
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p>Aucune demande en attente</p>
                </div>
              )}

              <h3 className="text-lg font-medium mt-8">Demandes traitées</h3>
              {courseRequests.filter((request) => request.status !== "En attente").length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {courseRequests
                    .filter((request) => request.status !== "En attente")
                    .map((request) => (
                      <Card
                        key={request.id}
                        className={`overflow-hidden border-l-4 ${
                          request.status === "Approuvée" ? "border-l-green-400" : "border-l-red-400"
                        }`}
                      >
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                              <Badge
                                variant="outline"
                                className={`mb-2 ${
                                  request.status === "Approuvée"
                                    ? "bg-green-50 text-green-700 border-green-200"
                                    : "bg-red-50 text-red-700 border-red-200"
                                }`}
                              >
                                {request.status}
                              </Badge>
                              <h4 className="text-lg font-bold">{request.formationName}</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Demande soumise le {new Date(request.demandeDate).toLocaleDateString("fr-FR")}
                              </p>
                              {request.status === "Approuvée" && (
                                <p className="text-sm text-green-600 mt-1">
                                  Vous pouvez maintenant accéder à cette formation
                                </p>
                              )}
                              {request.status === "Rejetée" && (
                                <p className="text-sm text-red-600 mt-1">
                                  Votre demande n'a pas été acceptée. Contactez-nous pour plus d'informations.
                                </p>
                              )}
                            </div>
                            {request.status === "Approuvée" ? (
                              <Button size="sm" asChild>
                                <a href={`/dashboard/watch/${request.formationId}`}>Accéder au cours</a>
                              </Button>
                            ) : (
                              <Button variant="outline" size="sm">
                                <a href={`/courses/${request.formationId}`}>Voir les détails</a>
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p>Aucune demande traitée</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}